import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Loader2, X } from 'lucide-react';
import DatePicker from '../DatePicker';
import { api } from '../../api/client';

/**
 * The certification-day ledger, on the equipment detail screen.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * Until API migration 0356 the only way to say "this tool ran 340 days before
 * it was in FieldIQ" was to type 340 into the Working Days Since Last
 * Inspection box in the edit modal. PR #501 gated that box to
 * {admin, office_staff, dispatcher} and recorded every change, but it is still
 * an overwrite: the number replaces the number, and nothing says where either
 * came from. 86 live units carry 4,736 days in exactly that state.
 *
 * These forms write an APPEND-ONLY ledger instead. Two shapes:
 *
 *   opening balance   one number and an as-of date, offered only while the
 *                     unit has no entries at all — an opening balance is by
 *                     definition the first thing said about a counter, and a
 *                     second one is a correction wearing the wrong name.
 *   historical day    one run off a paper log, repeatable, with the rig and
 *                     oil company a certificate reader will want beside it.
 *
 * ── NOTHING HERE EDITS. THAT IS THE WHOLE POINT ─────────────────────────────
 *
 * There is no Delete and the Edit button opens a CORRECTION dialog, which
 * writes a new row carrying a signed delta and the id of the entry it
 * supersedes. The original stays exactly as it was written. A cert-day count
 * goes on a document an oil company reads, and the question somebody
 * eventually asks is not "what is the number" but "who said so, and when did
 * it change" — an edit answers the first and destroys the second.
 */

export interface CertAdjustmentRecord {
  id: string;
  days: number;
  kind: 'opening_balance' | 'historical_day' | 'correction' | 'cycle_reset' | 'counter_set';
  workDate: string | null;
  certStartDateAtEntry: string | null;
  rigName: string | null;
  customerName: string | null;
  note: string | null;
  supersedesId: string | null;
  /** The correction that superseded THIS row. Null means it stands. */
  supersededById: string | null;
  source: 'manual' | 'import';
  counterBefore: number | null;
  counterAfter: number;
  actorEmail: string | null;
  createdAt: string;
  /**
   * When this entry was removed from the count (API 0390). NULL means it
   * stands.
   *
   * A removed entry is RETURNED by the ledger read — the one read in the API
   * that does not filter them — and rendered struck through with who and why.
   * It has left the counter, the usage log, the certification PDF and the CSV.
   * Optional on the type because a response from before 0390 carries none of
   * these, and a stale cached one will not either.
   */
  deletedAt?: string | null;
  deletedBy?: string | null;
  deletedByEmail?: string | null;
  deleteReason?: string | null;
}

/**
 * An EXHAUSTIVE map keyed on every kind the API can return, not a fallback that
 * echoes the stored value. `historical_day` shown to a user is a database
 * column leaking onto the screen, and a label that guesses is how a display bug
 * gets reported as a data bug.
 */
export const CERT_ADJUSTMENT_LABELS: Record<CertAdjustmentRecord['kind'], string> = {
  opening_balance: 'Opening balance',
  historical_day: 'Historical day',
  correction: 'Correction',
  // A counter typed into the equipment modal. Since API 0388 that write is a
  // ledger row rather than a silent overwrite, so it appears here — and it
  // must read as what it is, an office edit, not as a day the tool worked.
  counter_set: 'Count set',
  cycle_reset: 'Cycle reset',
};

export function certAdjustmentLabel(kind: string | null | undefined): string {
  return (kind && CERT_ADJUSTMENT_LABELS[kind as CertAdjustmentRecord['kind']]) || 'Manual entry';
}

/**
 * Does this unit's counter add up?
 *
 * `residual = counter − explained`, and BOTH SIGNS ARE REAL:
 *
 *   POSITIVE  the counter holds days no entry accounts for — residue from the
 *             overwrite door that predates this ledger. The API REFUSES a
 *             historical day onto it unless the operator confirms.
 *   NEGATIVE  the entries add up to MORE than the counter holds. The API's
 *             guard reads `residual > 0`, so nothing refuses this and nothing
 *             warns. It is produced by a counter written down without an entry
 *             beside it, and it is live: TNT's TN 1103 reads 73 against
 *             entries asserting 84.
 *
 * That asymmetry is the reason this is rendered rather than left in the API. A
 * number nothing warns about is a number somebody has to be able to LOOK at.
 */
export interface CertReconciliation {
  /** NULL means NEVER COUNTED — not zero. */
  counter: number | null;
  explained: number;
  residual: number;
  baseline: number;
  adjustedSince: number;
  ledgeredSince: number;
  anchorId: string | null;
  anchorKind: CertAdjustmentRecord['kind'] | null;
  anchorAt: string | null;
}

export interface CertLedgerData {
  adjustments: CertAdjustmentRecord[];
  /**
   * Absent until the API carrying it is deployed, and absent forever on a stale
   * cached response. Every consumer treats `null` as "not answered" and renders
   * nothing rather than a zero — the same rule the rest of this file follows
   * for an absent number.
   */
  reconciliation: CertReconciliation | null;
}

/**
 * The ledger for one unit. `enabled` carries the role gate: the API refuses
 * this read to anyone outside {admin, office_staff, dispatcher}, so firing it
 * for a field worker buys a guaranteed 403 in their console.
 *
 * NOT CYCLE-SCOPED, and that is the difference from the Usage Log beside it.
 * `listCertAdjustments` has never filtered on `cert_start_date_at_entry`, so
 * this returns every entry ever made against the unit. The Usage Log's
 * adjustment half IS scoped that way, which means moving a unit's cert start
 * date makes its hand-entered rows vanish from that table — and from the Edit
 * pencils that live on those rows. This is the surface that still shows them.
 */
export function useCertAdjustments(equipmentId: string | undefined, enabled: boolean) {
  return useQuery<CertLedgerData>({
    queryKey: ['cert-adjustments', equipmentId],
    queryFn: () => api.equipment.certAdjustments(equipmentId!).then((r: any) => {
      // Explicit unwrap. `r.data?.data || r.data` reads the ENVELOPE as data
      // when the API answers {success: true, data: null} — the object is
      // truthy, every field reads undefined, and nothing errors.
      const body = r.data;
      const payload = body && typeof body === 'object' && 'data' in body ? body.data : body;
      // `reconciliation` rides as a SIBLING of `data`, not inside it, which is
      // what let the API ship ahead of this screen without taking the Edit
      // pencils away in between. Read it off the envelope, and off nothing
      // else — a field that rides only inside `data` has historically never
      // rendered here.
      const reconciliation =
        body && typeof body === 'object' && body.reconciliation
          ? (body.reconciliation as CertReconciliation)
          : null;
      return {
        adjustments: Array.isArray(payload) ? (payload as CertAdjustmentRecord[]) : [],
        reconciliation,
      };
    }),
    enabled: !!equipmentId && enabled,
  });
}

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

/** Every query a counter move invalidates. Stated once so no caller forgets one. */
export function invalidateCertViews(qc: ReturnType<typeof useQueryClient>, equipmentId: string) {
  qc.invalidateQueries({ queryKey: ['cert-adjustments', equipmentId] });
  qc.invalidateQueries({ queryKey: ['equipment-usage', equipmentId] });
  qc.invalidateQueries({ queryKey: ['equipment', equipmentId] });
  qc.invalidateQueries({ queryKey: ['equipment'] });
}

// ────────────────────────────────────────────────────────────────────────────

interface EntryPanelProps {
  equipmentId: string;
  /**
   * Whether the unit already has an OPENING BALANCE — which is what gates the
   * opening-balance button, and is NOT the same as having entries.
   *
   * It used to be `adjustments.length > 0`, and since API 0388 that strands
   * people: the residual guard refuses a historical day onto a counter holding
   * unexplained days and tells the operator to record an opening balance
   * first — so a unit that already has historical days is exactly the case
   * where the button is most needed. The rule the server actually enforces is
   * one opening balance per unit (a partial unique index), so that is the rule
   * mirrored here.
   */
  hasOpeningBalance: boolean;
  /** The counter as it stands. null means never counted. */
  counter: number | null;
}

export const CertAdjustmentEntryPanel: React.FC<EntryPanelProps> = ({
  equipmentId, hasOpeningBalance, counter,
}) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState<'opening' | 'historical' | null>(null);

  const [days, setDays] = useState('');
  const [workDate, setWorkDate] = useState('');
  const [rigName, setRigName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [note, setNote] = useState('');

  /**
   * A refusal the person can answer, held rather than thrown away.
   *
   * Two of the server's 409s are OVERRIDABLE — the counter holds days nothing
   * explains, or this unit already has an entry on this date — and both carry
   * a sentence stating exactly what would be overridden. That sentence is the
   * whole value of the confirm, so it is kept and SHOWN rather than replaced
   * with "are you sure": the residual number and the existing entry are facts
   * the person needs in order to decide, and a generic prompt trains them to
   * click through.
   *
   * The third, `opening_balance_exists`, is NOT overridable and is rendered as
   * a correction prompt instead — see below.
   */
  const [pending, setPending] = useState<
    { code: string; message: string; payload: any } | null
  >(null);

  const reset = () => {
    setDays(''); setWorkDate(''); setRigName(''); setCustomerName(''); setNote('');
    setPending(null);
  };

  const save = useMutation({
    mutationFn: (payload: any) => api.equipment.addCertAdjustment(equipmentId, payload),
    onSuccess: (_res, vars: any) => {
      invalidateCertViews(qc, equipmentId);
      setPending(null);
      toast.success(vars.kind === 'opening_balance' ? 'Opening balance recorded' : 'Historical day recorded');
      // A historical day is repeatable by design — the office is working down a
      // paper log — so the form stays open and only the day-specific fields
      // clear. The rig and oil company usually repeat across a run of days.
      if (vars.kind === 'historical_day') {
        setDays(''); setWorkDate(''); setNote('');
      } else {
        reset(); setOpen(null);
      }
    },
    onError: (err: any, vars: any) => {
      const body = err?.response?.data;
      const code = body?.code;
      const message = body?.error;
      // A refusal the person can act on is a PANEL carrying the server's own
      // words, never a toast. A toast disappears, and these three all ask a
      // question — "override?", "correct the existing entry?" — that the reader
      // has to still be looking at when they answer it.
      if (message && (code === 'unexplained_residual'
                   || code === 'duplicate_work_date'
                   || code === 'opening_balance_exists')) {
        setPending({ code, message, payload: vars });
        return;
      }
      toast.error(message || 'Could not record the entry.');
    },
  });

  const submit = (kind: 'opening_balance' | 'historical_day') => {
    // Number(), not parseInt — '3.5' must be refused, not silently truncated
    // to 3. The server refuses it too; this is so the person sees why.
    const n = days.trim() === '' ? NaN : Number(days);
    if (!Number.isInteger(n)) {
      toast.error(kind === 'opening_balance'
        ? 'The total must be a whole number.'
        : 'Days must be a whole number.');
      return;
    }
    if (kind === 'historical_day' && !workDate) {
      toast.error('A historical day needs the date it was worked.');
      return;
    }
    setPending(null);
    save.mutate({
      // ── ABSOLUTE vs ADDITIVE, and the field names carry the difference ────
      //
      // Since API 0388 an opening balance states the TOTAL as of a date; the
      // server computes the delta. A historical day still adds. Sending `days`
      // for an opening balance is refused by the server rather than guessed at,
      // so the two arms are built separately here rather than sharing a key.
      ...(kind === 'opening_balance' ? { kind, total: n } : { kind, days: n }),
      workDate: workDate || null,
      rigName: rigName.trim() || null,
      customerName: customerName.trim() || null,
      note: note.trim() || null,
    } as any);
  };

  /** Re-send the refused entry with the one flag that answers this refusal. */
  const proceedAnyway = () => {
    if (!pending) return;
    const flag = pending.code === 'unexplained_residual'
      ? { acknowledgeUnexplainedResidual: true }
      : { allowDuplicateWorkDate: true };
    save.mutate({ ...pending.payload, ...flag });
  };

  return (
    <div className="mt-4 pt-4 border-t border-fieldiq-border">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-sm font-semibold text-white">Certification Day Entries</h4>
          <p className="text-xs text-fieldiq-muted mt-0.5">
            Days worked before this tool was tracked in FieldIQ. Recorded, never typed over.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Offered until the unit HAS one. A second opening balance is not an
              opening balance, it is a correction — and the server refuses it
              with a 409 that this panel renders as a correction prompt, so the
              two agree rather than the button being the only thing enforcing
              it. */}
          {!hasOpeningBalance && (
            <button
              type="button"
              onClick={() => setOpen(open === 'opening' ? null : 'opening')}
              className="flex items-center gap-1.5 bg-fieldiq-card border border-fieldiq-border text-white px-3 py-2 rounded-lg text-xs font-medium hover:border-fieldiq-accent transition-colors"
            >
              <Plus size={14} /> Set Opening Balance
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(open === 'historical' ? null : 'historical')}
            className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark px-3 py-2 rounded-lg text-xs font-semibold hover:bg-amber-400 transition-colors"
          >
            <Plus size={14} /> Add Historical Days
          </button>
        </div>
      </div>

      {/* ── A refusal the person can answer, in the server's own words ──────
          Rendered above both forms so it is the first thing read after a
          failed save, and so the numbers it quotes — the residual, the
          existing entry — are the server's rather than a copy this file
          would have to keep in step. */}
      {pending && (
        <div className="mt-3 bg-fieldiq-dark rounded-lg border border-amber-500/40 p-4">
          <div className="flex items-start justify-between gap-3">
            <p className="text-xs text-fieldiq-text leading-relaxed">{pending.message}</p>
            <button type="button" onClick={() => setPending(null)} className="text-fieldiq-muted hover:text-white shrink-0">
              <X size={16} />
            </button>
          </div>
          {pending.code === 'opening_balance_exists' ? (
            // NOT overridable, and offering a "proceed" here would be a lie —
            // the database has a unique index on it. The way forward is a
            // correction of the entry that already stands, which is what the
            // Edit button on that row opens.
            <p className="mt-3 text-xs text-fieldiq-muted">
              Find the opening balance in the list below and use <span className="text-white">Edit</span> to
              correct it. The original entry stays on the record; the correction
              is a new row beside it.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button" disabled={save.isPending} onClick={proceedAnyway}
                className="bg-fieldiq-accent text-fieldiq-dark px-4 py-2 rounded-lg text-xs font-semibold hover:bg-amber-400 transition-colors disabled:opacity-40"
              >
                {save.isPending ? <Loader2 size={14} className="animate-spin inline" /> : 'Record it anyway'}
              </button>
              <button
                type="button" onClick={() => setPending(null)}
                className="bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2 rounded-lg text-xs font-medium hover:border-fieldiq-accent transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {open === 'opening' && (
        <div className="mt-3 bg-fieldiq-dark rounded-lg border border-fieldiq-border p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-xs text-fieldiq-text">
              {/* `=== null`, not a falsy check. A counter of 0 is an answer
                  ("counted, nought used"), not an absence, and reading it as
                  one here would tell the operator the opposite of the truth. */}
              {counter === null
                ? 'This tool has no day count yet. Enter the total it had worked as of the date below.'
                : `This tool currently reads ${counter} day${counter === 1 ? '' : 's'}. An opening balance REPLACES that number.`}
            </p>
            <button type="button" onClick={() => { reset(); setOpen(null); }} className="text-fieldiq-muted hover:text-white">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              {/* "Total days as of", matching the API field exactly. Since
                  0388 this number REPLACES the counter rather than adding to
                  it, and the label is the only thing standing between the
                  operator and the arithmetic that produced TNT's 243. */}
              <label className={labelClass} htmlFor="cert-opening-total">Total days as of</label>
              <input id="cert-opening-total" className={inputClass} type="number" min={0} step={1} placeholder="340"
                value={days} onChange={(e) => setDays(e.target.value)} />
            </div>
            <div>
              {/* DatePicker renders its own control, so the association is
                  by id on the wrapper the picker labels rather than htmlFor. */}
              <label className={labelClass} htmlFor="cert-opening-date">As of date</label>
              <DatePicker id="cert-opening-date" value={workDate} onChange={setWorkDate} />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelClass} htmlFor="cert-opening-note">Note (optional)</label>
            <input id="cert-opening-note" className={inputClass} placeholder="Where this number came from — e.g. log book 3"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button
            type="button" disabled={save.isPending}
            onClick={() => submit('opening_balance')}
            className="mt-3 w-full sm:w-auto bg-fieldiq-accent text-fieldiq-dark px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-40"
          >
            {save.isPending ? <Loader2 size={14} className="animate-spin inline" /> : 'Record Opening Balance'}
          </button>
        </div>
      )}

      {open === 'historical' && (
        <div className="mt-3 bg-fieldiq-dark rounded-lg border border-fieldiq-border p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-xs text-fieldiq-text">
              One day the tool ran, off a paper log. The form stays open so a run
              of days can be entered one after another.
            </p>
            <button type="button" onClick={() => { reset(); setOpen(null); }} className="text-fieldiq-muted hover:text-white">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelClass} htmlFor="cert-hist-date">Work date</label>
              <DatePicker id="cert-hist-date" value={workDate} onChange={setWorkDate} />
            </div>
            <div>
              <label className={labelClass} htmlFor="cert-hist-days">Days</label>
              <input id="cert-hist-days" className={inputClass} type="number" min={1} step={1} placeholder="1"
                value={days} onChange={(e) => setDays(e.target.value)} />
            </div>
            <div>
              <label className={labelClass} htmlFor="cert-hist-rig">Rig (optional)</label>
              <input id="cert-hist-rig" className={inputClass} placeholder="Rig 17" value={rigName} onChange={(e) => setRigName(e.target.value)} />
            </div>
            <div>
              <label className={labelClass} htmlFor="cert-hist-customer">Oil company (optional)</label>
              <input id="cert-hist-customer" className={inputClass} placeholder="Cenovus" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelClass} htmlFor="cert-hist-note">Note (optional)</label>
            <input id="cert-hist-note" className={inputClass} placeholder="Log book reference" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <button
            type="button" disabled={save.isPending}
            onClick={() => submit('historical_day')}
            className="mt-3 w-full sm:w-auto bg-fieldiq-accent text-fieldiq-dark px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-40"
          >
            {save.isPending ? <Loader2 size={14} className="animate-spin inline" /> : 'Add Day'}
          </button>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────

interface CorrectionDialogProps {
  equipmentId: string;
  entry: CertAdjustmentRecord;
  onClose: () => void;
}

/**
 * Correct one entry.
 *
 * Shows OLD → NEW, because the thing the operator is about to do is change a
 * compliance number and the size of the change is the part worth seeing before
 * they commit. A reason is required — an original entry explains itself with a
 * date and a rig, a correction does not, and "340 became 320" with no sentence
 * beside it leaves the next reader unable to tell a typo from a recount.
 */
export const CertCorrectionDialog: React.FC<CorrectionDialogProps> = ({ equipmentId, entry, onClose }) => {
  const qc = useQueryClient();
  const [days, setDays] = useState(String(entry.days));
  const [reason, setReason] = useState('');

  const parsed = days.trim() === '' ? NaN : Number(days);
  const valid = Number.isInteger(parsed) && parsed !== entry.days && reason.trim() !== '';
  const delta = Number.isInteger(parsed) ? parsed - entry.days : null;

  const save = useMutation({
    mutationFn: () => api.equipment.correctCertAdjustment(equipmentId, entry.id, {
      days: parsed,
      reason: reason.trim(),
      workDate: entry.workDate,
      rigName: entry.rigName,
      customerName: entry.customerName,
    }),
    onSuccess: () => {
      invalidateCertViews(qc, equipmentId);
      toast.success('Correction recorded');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Could not record the correction.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-label="Correct certification entry">
      <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border w-full max-w-lg">
        <div className="p-5 border-b border-fieldiq-border flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-white">Correct this entry</h3>
            <p className="text-xs text-fieldiq-muted mt-0.5">
              {certAdjustmentLabel(entry.kind)}
              {entry.workDate ? ` · ${entry.workDate}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-fieldiq-muted hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* A NEW ROW, not an edit. Said in the dialog rather than only in a
              comment, because the operator's mental model of an "Edit" button
              is that the old value goes away — and here it emphatically does
              not. */}
          <p className="text-xs text-fieldiq-text bg-fieldiq-dark rounded-lg px-3 py-2.5 border border-fieldiq-border">
            This records a new correcting entry. The original stays in the log,
            marked as corrected, so the certification record shows what changed
            and why.
          </p>

          <div>
            <label className={labelClass} htmlFor="cert-correction-days">Corrected days</label>
            <input id="cert-correction-days" className={inputClass} type="number" step={1} value={days}
              onChange={(e) => setDays(e.target.value)} autoFocus />
          </div>

          <div className="flex items-center gap-3 text-sm">
            <span className="text-fieldiq-muted">Old</span>
            <span className="font-bold text-white tabular-nums">{entry.days}</span>
            <span className="text-fieldiq-muted">→</span>
            <span className="text-fieldiq-muted">New</span>
            <span className="font-bold text-fieldiq-accent tabular-nums">
              {Number.isInteger(parsed) ? parsed : '—'}
            </span>
            {delta !== null && delta !== 0 && (
              <span className={`text-xs font-medium ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                ({delta > 0 ? '+' : '−'}{Math.abs(delta)} day{Math.abs(delta) === 1 ? '' : 's'})
              </span>
            )}
          </div>

          <div>
            <label className={labelClass} htmlFor="cert-correction-reason">Reason (required)</label>
            <textarea id="cert-correction-reason" className={inputClass} rows={2} value={reason}
              placeholder="Recount against log book 3 — two days double-entered"
              onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>

        <div className="p-5 border-t border-fieldiq-border flex gap-3">
          <button type="button" onClick={onClose}
            className="flex-1 bg-fieldiq-card border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium hover:border-fieldiq-muted transition-colors">
            Cancel
          </button>
          <button type="button" disabled={!valid || save.isPending} onClick={() => save.mutate()}
            className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {save.isPending ? <Loader2 size={14} className="animate-spin inline" /> : 'Record Correction'}
          </button>
        </div>
      </div>
    </div>
  );
};
